import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHeadset,
  FaEnvelope,
  FaPhone,
  FaComments,
  FaQuestionCircle,
  FaClock,
  FaChevronDown,
  FaCheckCircle,
} from "react-icons/fa";
import generalApi from "../api/generalApi";

const CustomerSupport = () => {
  const [activeItem, setActiveItem] = useState(null);
  const [faqs, setFaqs] = useState([
    {
      question: "I need to cancel or modify my booking.",
      answer:
        "Booking modifications and cancellations are subject to the hotel's policy. Please check the 'My Bookings' section of your profile to see your options. For additional assistance, please contact us with your Booking ID.",
    },
    {
      question: "My payment failed. What should I do?",
      answer:
        "If your payment was unsuccessful, please check that all your card details are correct and that you have sufficient funds. You can try to book again or use an alternative payment method. If the problem persists, please contact our support team.",
    },
    {
      question: "How can I check the status of my booking?",
      answer:
        "You can view the real-time status of all your reservations in your user profile under the 'My Bookings' tab. You will also receive an email confirmation once your booking is finalized.",
    },
    {
      question: "What if I arrive late to the hotel?",
      answer:
        "Most hotels have 24-hour check-in available. However, we recommend informing the hotel in advance if you expect to arrive late. You can find the hotel contact details in your booking confirmation email.",
    },
  ]);

  useEffect(() => {
    generalApi.getFaqs("support").then((data) => {
      if (Array.isArray(data) && data.length > 0) {
        setFaqs(data);
      }
    }).catch(() => {});
  }, []);

  const toggleAccordion = (index) => {
    setActiveItem(activeItem === index ? null : index);
  };

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        minHeight: "100vh",
        paddingBottom: "50px",
        paddingTop: "25px",
      }}
    >
      {/* Enhanced Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
          color: "#fff",
          padding: "50px 20px 35px 20px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Circles */}
        <div
          style={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "60px",
              height: "60px",
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              marginBottom: "16px",
            }}
          >
            <FaHeadset style={{ fontSize: "1.8rem" }} />
          </div>
          <h1
            style={{
              fontSize: "clamp(2rem, 5vw, 2.5rem)",
              fontWeight: "800",
              marginBottom: "10px",
              letterSpacing: "-0.8px",
              lineHeight: "1.1",
            }}
          >
            Customer Support
          </h1>
          <p
            style={{
              fontSize: "0.95rem",
              opacity: 0.95,
              fontWeight: "500",
              maxWidth: "600px",
              margin: "0 auto",
              lineHeight: "1.5",
            }}
          >
            We're here to help you 24/7. Our support team is ready to assist
            with any questions or issues.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{ maxWidth: "1100px", margin: "0 auto", padding: "40px 20px" }}
      >
        {/* Contact Methods Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "20px",
            marginBottom: "50px",
          }}
        >
          {[
            {
              icon: <FaComments />,
              title: "Live Chat",
              desc: "Get instant support from our team. Available 24/7.",
              action: "Start Chatting Now",
              color: "#EE2E24",
            },
            {
              icon: <FaEnvelope />,
              title: "Email Us",
              desc: "Send us your questions and we'll respond within 24 hours.",
              action: "booktrivana@gmail.com",
              color: "#FFC400",
            },
            {
              icon: <FaPhone />,
              title: "Call Support",
              desc: "Talk to our support agent for immediate assistance.",
              action: "+91 9257734350",
              color: "#EE2E24",
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
              style={{
                background: "#FFFFFF",
                padding: "28px",
                borderRadius: "16px",
                textAlign: "center",
                border: "1px solid rgba(238, 46, 36, 0.1)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 30px rgba(238, 46, 36, 0.15)";
                e.currentTarget.style.borderColor = item.color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)";
                e.currentTarget.style.borderColor = "rgba(238, 46, 36, 0.1)";
              }}
            >
              <div
                style={{
                  fontSize: "2.2rem",
                  color: item.color,
                  marginBottom: "16px",
                  display: "inline-flex",
                  padding: "16px",
                  background: `${item.color}15`,
                  borderRadius: "50%",
                }}
              >
                {item.icon}
              </div>
              <h3
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  color: "#2E2E2E",
                  marginBottom: "8px",
                  letterSpacing: "-0.3px",
                }}
              >
                {item.title}
              </h3>
              <p
                style={{
                  color: "#2E2E2E",
                  fontSize: "0.85rem",
                  marginBottom: "14px",
                  opacity: 0.7,
                  lineHeight: "1.5",
                }}
              >
                {item.desc}
              </p>
              <p
                style={{
                  color: item.color,
                  fontSize: "0.9rem",
                  fontWeight: "700",
                }}
              >
                {item.action}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Quick Help Hub */}
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: "800",
              color: "#2E2E2E",
              marginBottom: "24px",
              textAlign: "center",
              letterSpacing: "-0.8px",
            }}
          >
            Quick Help Hub
          </h2>

          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "28px",
              boxShadow: "0 6px 20px rgba(238, 46, 36, 0.08)",
              border: "1px solid rgba(238, 46, 36, 0.1)",
            }}
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                style={{
                  borderBottom:
                    index < faqs.length - 1 ? "1px solid #F5F5F5" : "none",
                  paddingBottom: index < faqs.length - 1 ? "16px" : "0",
                  marginBottom: index < faqs.length - 1 ? "16px" : "0",
                }}
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    outline: "none",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      color: "#2E2E2E",
                      flex: 1,
                      paddingRight: "16px",
                      letterSpacing: "-0.3px",
                    }}
                  >
                    {faq.question}
                  </span>
                  <span
                    style={{
                      color: "#EE2E24",
                      fontSize: "1.2rem",
                      transition: "transform 0.3s ease",
                      transform:
                        activeItem === index
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      display: "flex",
                    }}
                  >
                    <FaChevronDown />
                  </span>
                </button>

                <AnimatePresence>
                  {activeItem === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <p
                        style={{
                          fontSize: "0.85rem",
                          color: "#2E2E2E",
                          opacity: 0.8,
                          lineHeight: "1.6",
                          margin: "8px 0 0 0",
                          fontWeight: "500",
                        }}
                      >
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Additional Info */}
          <div
            style={{
              marginTop: "32px",
              textAlign: "center",
              padding: "24px",
              background:
                "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
              borderRadius: "12px",
              border: "1px solid rgba(238, 46, 36, 0.1)",
            }}
          >
            <FaCheckCircle
              style={{
                fontSize: "2rem",
                color: "#EE2E24",
                marginBottom: "12px",
              }}
            />
            <p
              style={{
                fontSize: "0.9rem",
                color: "#2E2E2E",
                opacity: 0.8,
                margin: 0,
                fontWeight: "500",
                lineHeight: "1.6",
              }}
            >
              For any other inquiries, please feel free to reach out to us at
              any time. We are committed to providing you with the best
              experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSupport;
